/**
 * Where an authenticated principal lands on `/`.
 *
 * Previously `/` redirected unconditionally to `/dashboard`. That is right for
 * most people and wrong for anyone granted a single module: the dashboard is its
 * own module, so a principal holding (say) Billing alone was bounced straight to
 * `/forbidden` at login — and the only link on that page pointed back at
 * `/dashboard`, so it was a loop. The sidebar showed Billing correctly, which
 * made the app reachable only by clicking past a permission error, or by typing
 * the URL.
 *
 * So the landing page is derived from the same nav model the sidebar renders:
 * the first entry the principal may actually see. A principal with no module at
 * all still goes to `/forbidden` — for them it is the truth, not a dead end.
 */
import type { JSX } from "react";
import { Navigate } from "react-router-dom";
import { firstPermittedRoute } from "../components/layout/nav";
import { useAccess } from "./useAccess";
import { useAuth } from "./AuthContext";

export function LandingRoute(): JSX.Element | null {
  const { isLoading } = useAuth();
  const { atLeast, can } = useAccess();

  // `useAccess` fails closed while /me is in flight, so redirecting now would
  // send everyone to /forbidden for a frame and then bounce them again.
  if (isLoading) return null;

  return <Navigate to={firstPermittedRoute({ atLeast, can }) ?? "/forbidden"} replace />;
}
